/* ──────────────────────────────────────────────────────────────────────────
 * Evaluation & Trust Intelligence — §15, the evidence envelope.
 *
 * The brief lists sixteen fields that every material recommendation must
 * carry, and names what they add up to:
 *
 *   "This gives us Decision Provenance rather than merely an AI output."
 *
 * The distinction is the whole section. An AI output is a conclusion. A
 * decision provenance record is a conclusion plus everything needed to
 * challenge it later: what it was derived from, what else was considered,
 * who decided, and whether it worked.
 *
 * ── THE DESIGN RULE ──
 * Every one of the sixteen fields is present in every envelope. A field
 * that cannot be filled is present and NULL with a stated reason, never
 * omitted. Omission is the failure mode that matters here: an envelope
 * missing "outcome" looks complete to a reader who does not know the field
 * exists, whereas `outcome: null, reason: "not yet verified"` cannot be
 * misread. `completeness` counts this so a reviewer can see at a glance
 * how much of the record is actually there.
 *
 * ── WHAT THIS IS NOT ──
 * It is not an audit record, and says so in the envelope itself. The
 * events it draws on live in browser storage, which anyone can clear. The
 * hash chain detects accidental corruption, not a determined edit. §17
 * names the deployment layer that would own real immutability.
 * ────────────────────────────────────────────────────────────────────────── */

import { TRUST, CLASSIFICATION } from "../geo/data/provenance";
import { STATUS } from "./incidentMachine";

/* Model and rule versions. Hand-maintained and therefore fallible, but a
 * wrong version string is still better than none: without it, a decision
 * made last week cannot be reproduced against the logic that made it. */
export const LOGIC_VERSIONS = {
  crowdSafety: "crowdSafety@1.2.0",
  riskEngine: "riskEngine@1.1.0",
  liveModel: "liveModel@1.3.0",
  multiHazard: "multiHazard@1.1.0",
  serviceStress: "serviceStress@1.1.0",
  interventions: "interventions@1.0.0",
  emergencyResponse: "emergencyResponse@1.0.0",
  mobilityChain: "mobilityChain@1.0.0",
  twin: "twin@1.0.0",
};

/* The sixteen fields, in the brief's order, so coverage is checkable line
 * by line against the source document. */
export const ENVELOPE_FIELDS = [
  "incidentId", "sourceSystems", "sensor", "timestamp", "location",
  "riskClassification", "logicVersion", "confidence", "supportingEvidence",
  "recommendation", "alternativeOptions", "officerDecision",
  "departmentAssignment", "executionTimestamp", "outcome", "closureAuthority",
];

const missing = (reason) => ({ value: null, available: false, reason });
const present = (value) => ({ value, available: true, reason: null });

/* ── Build ─────────────────────────────────────────────────────────────
 * `incident` and `events` come from the spine. The engine outputs are
 * optional: an envelope built without the simulator simply records that
 * alternatives were not evaluated, which is itself worth knowing. */
export function buildEvidenceEnvelope({
  incident,
  events = [],
  simulation = null,
  responsePlan = null,
  hazardPicture = null,
  observations = [],
  chainVerified = null,
} = {}) {
  if (!incident) {
    return { available: false, reason: "No incident supplied." };
  }

  const ev = (type) => events.filter((e) => (e.type ?? e.to) === type);
  const first = (type) => ev(type)[0] ?? null;

  const authorised = first(STATUS.AUTHORISED) ?? first("authorised");
  const dispatched = first(STATUS.DISPATCHED) ?? first("dispatched");
  const resolved = first(STATUS.RESOLVED) ?? first("resolved");

  /* 1. Incident ID */
  const incidentId = incident.id ? present(incident.id) : missing("Incident has no identifier.");

  /* 2. Source systems — what actually contributed, named honestly. */
  const sources = [];
  sources.push({ system: "liveModel", kind: "SIMULATION", note: "Crowd state is modelled, not measured." });
  if (hazardPicture) sources.push({ system: "multiHazard", kind: "DERIVED", note: "Correlated from modelled inputs." });
  if (simulation) sources.push({ system: "interventions", kind: "SIMULATION", note: "Options evaluated by re-running the model." });
  if (responsePlan) sources.push({ system: "emergencyResponse", kind: "DERIVED", note: "Responder selection over invented unit positions." });
  const sourceSystems = present(sources);

  /* 3. Camera / sensor — the honest null. There is no sensor. */
  const sensor = missing(
    "No camera or sensor contributed to this record. The platform has no video or IoT integration; every input is modelled.",
  );

  /* 4. Timestamp */
  const timestamp = incident.detectedAt ?? incident.createdAt
    ? present(incident.detectedAt ?? incident.createdAt)
    : missing("No detection timestamp was recorded.");

  /* 5. Location */
  const location = incident.sector || incident.coord
    ? present({ sector: incident.sector ?? null, coord: incident.coord ?? null })
    : missing("No location was recorded for this incident.");

  /* 6. Risk classification */
  const sectorHazard = hazardPicture?.sectors?.find((s) => s.id === incident.sector) ?? null;
  const riskClassification = sectorHazard
    ? present({
        level: sectorHazard.level,
        compound: sectorHazard.compound,
        strongest: sectorHazard.strongest?.key ?? null,
        /* Coverage travels with the classification. A risk level derived
         * from twelve of sixteen hazards is a different claim from one
         * derived from all of them. */
        hazardCoverage: sectorHazard.coverage ?? null,
      })
    : missing("No hazard correlation was supplied for this incident's sector.");

  /* 7. Model / rule version */
  const logicVersion = present({
    ...LOGIC_VERSIONS,
    note: "Versions are hand-maintained. A decision cannot be reproduced without knowing which logic produced it.",
  });

  /* 8. Confidence — deliberately provenance, not a percentage. */
  const confidence = present({
    provenance: TRUST.SIMULATED,
    numericScore: null,
    reason:
      "No numeric confidence is given. Every input is simulated, so a percentage would express precision about a figure that has no measurement behind it.",
  });

  /* 9. Supporting evidence */
  const evidence = [];
  if (sectorHazard) {
    evidence.push({ kind: "hazard_correlation", summary: sectorHazard.narrative });
  }
  if (simulation) {
    evidence.push({
      kind: "intervention_simulation",
      summary: simulation.noOptionHelps
        ? "No simulated intervention reduced the projected peak."
        : `Simulated ${simulation.options.length} options against a do-nothing baseline over ${simulation.horizon} minutes.`,
    });
  }
  if (responsePlan) {
    evidence.push({
      kind: "response_plan",
      summary: `Primary responder ${responsePlan.primaryResponder?.id ?? "none"} at ETA ${responsePlan.estimatedResponseMinutes ?? "unknown"} min.`,
      warnings: responsePlan.warnings,
    });
  }
  const supportingEvidence = evidence.length
    ? present(evidence)
    : missing("No engine output was attached to this record.");

  /* 10. Recommendation */
  const rec = simulation?.recommended ?? null;
  const recommendation = rec
    ? present({
        id: rec.id, key: rec.key, label: rec.label,
        projectedEffect: {
          deltaPeakDensity: rec.deltaPeakDensity,
          deltaOccupancyPct: rec.deltaOccupancyPct,
        },
        lagMin: rec.lagMin,
        cost: rec.cost,
      })
    : missing(
        simulation?.noOptionHelps
          ? "The simulator evaluated all options and none reduced the projected peak. No recommendation was made."
          : "No intervention simulation was attached to this record.",
      );

  /* 11. Alternative options — the field most often quietly dropped, and
   * the one that makes a decision reviewable. */
  const alternativeOptions = simulation
    ? present(simulation.options
        .filter((o) => o.id !== rec?.id)
        .map((o) => ({
          id: o.id, label: o.label, available: o.available,
          unavailableReason: o.unavailableReason,
          deltaPeakDensity: o.deltaPeakDensity,
          rejected: true,
        })))
    : missing("No alternatives were evaluated, so this decision cannot be compared against what else was possible.");

  /* 12. Officer decision */
  const officerDecision = authorised
    ? present({
        decision: authorised.decision ?? "authorised",
        at: authorised.at ?? authorised.timestamp ?? null,
        note: authorised.note ?? null,
        /* Officer identity is RESTRICTED and is redacted for any role
         * below command by §17's filter. It is recorded, not published. */
        authorisedBy: authorised.actor ?? authorised.by ?? null,
        classification: CLASSIFICATION.RESTRICTED,
      })
    : missing("No authorisation has been recorded. The incident has not been through human decision.");

  /* 13. Department assignment */
  const assignments = incident.assignments ?? incident.departments ?? null;
  const departmentAssignment = assignments?.length
    ? present(assignments)
    : missing("No department has been assigned.");

  /* 14. Execution timestamp */
  const executionTimestamp = dispatched
    ? present(dispatched.at ?? dispatched.timestamp ?? null)
    : missing("Nothing has been dispatched, so there is no execution time.");

  /* 15. Outcome — verified, not asserted. */
  const outcome = resolved
    ? present({
        resolvedAt: resolved.at ?? resolved.timestamp ?? null,
        observations: observations.length,
        verified: observations.length >= 2,
        caveat:
          "A change in conditions after an intervention is not proof the intervention caused it. Confounders are not controlled for.",
      })
    : missing("The incident is not resolved. Outcome cannot be reported for an open incident.");

  /* 16. Closure authority */
  const closureAuthority = resolved
    ? present({
        by: resolved.actor ?? resolved.by ?? null,
        at: resolved.at ?? resolved.timestamp ?? null,
        classification: CLASSIFICATION.RESTRICTED,
      })
    : missing("The incident has not been closed, so no closing authority has been recorded.");

  const envelope = {
    incidentId, sourceSystems, sensor, timestamp, location,
    riskClassification, logicVersion, confidence, supportingEvidence,
    recommendation, alternativeOptions, officerDecision,
    departmentAssignment, executionTimestamp, outcome, closureAuthority,
  };

  const filled = ENVELOPE_FIELDS.filter((f) => envelope[f].available);

  return {
    available: true,
    incidentId: incident.id,
    fields: envelope,
    /* Completeness, counted rather than felt. An envelope at 7/16 is a
     * live incident mid-workflow; at 16/16 it is a closed decision with a
     * verified outcome. Both are legitimate; confusing them is not. */
    completeness: {
      filled: filled.length,
      total: ENVELOPE_FIELDS.length,
      pct: Math.round((filled.length / ENVELOPE_FIELDS.length) * 100),
      missing: ENVELOPE_FIELDS.filter((f) => !envelope[f].available)
        .map((f) => ({ field: f, reason: envelope[f].reason })),
    },
    integrity: {
      chainVerified,
      /* Stated in the record itself, so an exported envelope cannot be
       * presented as something it is not. */
      isAuditRecord: false,
      caveat:
        "This is NOT an audit record. The events behind it are held in browser storage, which the user can clear at any time. "
        + "The hash chain detects accidental corruption, not deliberate editing. A real deployment writes these to an append-only store outside the client.",
    },
    trust: TRUST.SIMULATED,
  };
}

/* Decision provenance across many incidents — what an oversight body would
 * actually ask for. */
export function provenanceSummary(envelopes = []) {
  if (!envelopes.length) {
    return { measurable: false, reason: "No envelopes to summarise." };
  }
  const complete = envelopes.filter((e) => e.completeness.pct === 100);
  const withAlternatives = envelopes.filter((e) => e.fields.alternativeOptions.available);
  const withOutcome = envelopes.filter((e) => e.fields.outcome.available);

  /* Which field is missing most often. The single most useful number for
   * improving a decision record, and nobody ever computes it. */
  const gapCounts = {};
  envelopes.forEach((e) => {
    e.completeness.missing.forEach((m) => {
      gapCounts[m.field] = (gapCounts[m.field] ?? 0) + 1;
    });
  });
  const weakest = Object.entries(gapCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([field, count]) => ({ field, missingIn: count }));

  return {
    measurable: true,
    total: envelopes.length,
    complete: complete.length,
    averageCompleteness: Math.round(
      envelopes.reduce((a, e) => a + e.completeness.pct, 0) / envelopes.length,
    ),
    decisionsWithAlternativesRecorded: withAlternatives.length,
    decisionsWithVerifiedOutcome: withOutcome.length,
    weakestFields: weakest,
  };
}

export const EVIDENCE_ASSUMPTIONS = [
  "All sixteen fields are present in every envelope. A field that cannot be filled is null with a reason, never omitted — an envelope missing a field looks complete to a reader who does not know the field exists.",
  "No numeric confidence score is produced. Every input is simulated, so a percentage would express precision about a figure with no measurement behind it.",
  "Alternative options are recorded, including the ones the simulator marked unavailable. A decision that does not record what else was possible cannot be reviewed.",
  "Outcome is reported only for resolved incidents, and carries the caveat that a change after an intervention is not proof the intervention caused it.",
  "Officer identity is recorded but classified RESTRICTED, and is removed by the §17 redaction filter for any role below command.",
  "This is explicitly not an audit record, and the envelope says so in its own integrity block so an export cannot be presented as one.",
];
